import * as R from 'ramda';
import { v4 as uuid } from 'uuid';

import { ConversionUnit, ConversionUnitInput } from '@atom/types/schema';

export const DUPLICATE_CONVERSION_ERROR = 'This conversion already exists.';
export const INVALID_CONVERSION_ERROR =
  'Enter a number greater than 0 with up to 4 decimals.';

const MAX_DECIMALS = 4;

const countDecimals = (value: number): number =>
  (String(value).split('.')[1] ?? '').length;

/**
 * returns an error message for an invalid conversion, or null when it is valid.
 * excludeId skips the row being edited when checking for duplicates.
 */
export const getConversionUnitError = (
  conversionUnits: ConversionUnit[],
  unit: string,
  conversionNumber: number | null,
  excludeId?: string,
): string | null => {
  if (
    conversionNumber === null ||
    !Number.isFinite(conversionNumber) ||
    conversionNumber <= 0 ||
    countDecimals(conversionNumber) > MAX_DECIMALS
  ) {
    return INVALID_CONVERSION_ERROR;
  }

  const isDuplicate = conversionUnits.some(
    conversionUnit =>
      conversionUnit.id !== excludeId &&
      conversionUnit.unit === unit &&
      conversionUnit.conversionNumber === conversionNumber,
  );

  return isDuplicate ? DUPLICATE_CONVERSION_ERROR : null;
};

/**
 * how many conversion units equal one base unit, rounded to the same number of
 * decimals a conversion can have. A conversion of 8 returns 0.125.
 */
export const getReverseConversion = (conversionNumber: number): number =>
  Number((1 / conversionNumber).toFixed(MAX_DECIMALS));

/**
 * prepends an unsaved row with a client id, since the newest rows show first.
 * The first row added becomes the default.
 */
export const addConversionUnit = (
  conversionUnits: ConversionUnit[],
  unit: string,
  conversionNumber: number,
): ConversionUnit[] => [
  {
    id: uuid(),
    unit,
    conversionNumber,
    active: true,
    isDefault: conversionUnits.length === 0,
  },
  ...conversionUnits,
];

/**
 * newest first. Unsaved rows have no createdDate, so they stay on top in their
 * current order.
 */
export const sortConversionUnits = (
  conversionUnits: ConversionUnit[],
): ConversionUnit[] =>
  R.sortWith(
    [R.descend(conversionUnit => conversionUnit.createdDate ?? Infinity)],
    conversionUnits,
  );

export const updateConversionUnit = (
  conversionUnits: ConversionUnit[],
  id: string,
  changes: Partial<Pick<ConversionUnit, 'unit' | 'conversionNumber'>>,
): ConversionUnit[] =>
  conversionUnits.map(conversionUnit =>
    conversionUnit.id === id
      ? { ...conversionUnit, ...changes }
      : conversionUnit,
  );

/**
 * a lone remaining row always becomes the default
 */
export const removeConversionUnit = (
  conversionUnits: ConversionUnit[],
  id: string,
): ConversionUnit[] => {
  const remaining = conversionUnits.filter(
    conversionUnit => conversionUnit.id !== id,
  );

  return remaining.length === 1
    ? [{ ...remaining[0], isDefault: true }]
    : remaining;
};

/**
 * the default unit can't be inactive, so making a unit the default also activates it
 */
export const setDefaultConversionUnit = (
  conversionUnits: ConversionUnit[],
  id: string,
): ConversionUnit[] =>
  conversionUnits.map(conversionUnit =>
    conversionUnit.id === id
      ? { ...conversionUnit, isDefault: true, active: true }
      : { ...conversionUnit, isDefault: false },
  );

export const setConversionUnitActive = (
  conversionUnits: ConversionUnit[],
  id: string,
  active: boolean,
): ConversionUnit[] =>
  conversionUnits.map(conversionUnit =>
    conversionUnit.id === id ? { ...conversionUnit, active } : conversionUnit,
  );

/**
 * drops client ids and server-assigned fields, since the server assigns both on create
 */
export const toConversionUnitInputs = (
  conversionUnits: ConversionUnit[],
): ConversionUnitInput[] =>
  conversionUnits.map(({ unit, conversionNumber, active, isDefault }) => ({
    unit,
    conversionNumber,
    active,
    isDefault,
  }));
